import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Pagination,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
} from '@mui/material';
import ResponsiveDataGrid from '../components/ResponsiveDataGrid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Security,
  ExpandMore,
  Person,
  History,
  Storage,
  Search,
  FilterList,
  Visibility,
  Edit,
  Delete,
  Add,
  Payment,
  Warning,
  Event,
  AccountCircle,
  Close,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import { ACCENTS } from '../theme/accents';

// audit_logs stores raw identifiers (UPDATE_ATTENDANCE, meeting_attendance).
// Members read this page too, so render them as words; the stored value is
// still available on hover.
const humaniseAction = (value) =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');

const humaniseTable = (value) => String(value || '').replace(/_/g, ' ');

// DataGrid renders this instead of a bare "No rows" line.
const NoRows = () => (
  <EmptyState
    icon={<Security />}
    accent={ACCENTS.indigo}
    title={'No matching activity'}
    description={'Nothing was logged for the selected filters. Widen the date range or clear a filter to see more.'}
  />
);

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    userId: '',
    tableName: '',
    action: '',
    fromDate: null,
    toDate: null,
    limit: 100,
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchAuditLogs();
    fetchSummary();
    fetchUsers();
  }, [filters]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.tableName) params.append('tableName', filters.tableName);
      if (filters.action) params.append('action', filters.action);
      if (filters.fromDate) params.append('fromDate', filters.fromDate.format('YYYY-MM-DD'));
      if (filters.toDate) params.append('toDate', filters.toDate.format('YYYY-MM-DD'));
      if (filters.limit) params.append('limit', filters.limit);
      
      const response = await axios.get(`/api/audit?${params}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get('/api/audit/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch audit summary');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('CREATE')) return <Add color="success" />;
    if (action.includes('UPDATE')) return <Edit color="primary" />;
    if (action.includes('DELETE')) return <Delete color="error" />;
    if (action.includes('PAY')) return <Payment color="warning" />;
    return <History color="action" />;
  };

  const getActionColor = (action) => {
    if (action.includes('CREATE')) return 'success';
    if (action.includes('UPDATE')) return 'primary';
    if (action.includes('DELETE')) return 'error';
    if (action.includes('PAY')) return 'warning';
    return 'default';
  };

  const getTableIcon = (tableName) => {
    switch (tableName) {
      case 'payments':
        return <Payment />;
      case 'penalties':
        return <Warning />;
      case 'meetings':
        return <Event />;
      case 'users':
        return <Person />;
      default:
        return <History />;
    }
  };

  const formatJsonData = (data) => {
    if (!data || data === null) return 'No data available';
    try {
      if (typeof data === 'string') {
        // Try to parse if it's a JSON string
        const parsed = JSON.parse(data);
        return JSON.stringify(parsed, null, 2);
      }
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const renderJsonViewer = (data, type = 'new') => {
    const formattedData = formatJsonData(data);
    const isEmptyData = !data || data === null || formattedData === 'No data available';
    
    if (isEmptyData) {
      return (
        <Box sx={{ 
          p: 3, 
          textAlign: 'center',
          color: 'text.secondary',
          fontStyle: 'italic'
        }}>
          <Typography variant="body2">
            {type === 'old' ? 'No previous values (this was a CREATE operation)' : 'No new values recorded'}
          </Typography>
        </Box>
      );
    }

    // Parse the JSON to create a more readable display
    let parsedData;
    try {
      parsedData = typeof data === 'string' ? JSON.parse(data) : data;
    } catch {
      parsedData = data;
    }

    return (
      <Box>
        {/* Formatted JSON Display */}
        <pre style={{ 
          fontSize: '13px', 
          overflow: 'auto',
          margin: 0,
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          color: type === 'old' ? '#ff5722' : '#4caf50',
          lineHeight: 1.4,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          backgroundColor: type === 'old' ? 'rgba(255, 87, 34, 0.05)' : 'rgba(76, 175, 80, 0.05)',
          padding: '8px',
          borderRadius: '4px'
        }}>
          {formattedData}
        </pre>
        
        {/* Key-Value Table for better readability */}
        {typeof parsedData === 'object' && parsedData !== null && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Field Changes:
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(parsedData).map(([key, value]) => (
                <Grid item xs={12} key={key}>
                  <Box sx={{ 
                    display: 'flex', 
                    p: 1, 
                    bgcolor: 'action.hover', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'bold', 
                      minWidth: '120px',
                      color: 'primary.main'
                    }}>
                      {key}:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      ml: 1, 
                      wordBreak: 'break-word',
                      fontFamily: 'monospace'
                    }}>
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 60,
      renderCell: (params) => (
        <Typography variant="caption" color="textSecondary">
          #{params.value}
        </Typography>
      ),
    },
    { 
      field: 'created_at', 
      headerName: 'Timestamp', 
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {dayjs(params.value).format('MMM D, YYYY')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {dayjs(params.value).format('HH:mm:ss')}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'user_name', 
      headerName: 'User', 
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
            {params.value?.charAt(0) || 'S'}
          </Avatar>
          <Typography variant="body2">
            {params.value || 'System'}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'action', 
      headerName: 'Action', 
      width: 180,
      renderCell: (params) => (
        <Tooltip title={params.value} enterDelay={500}>
          <Chip
            icon={getActionIcon(params.value)}
            label={humaniseAction(params.value)}
            color={getActionColor(params.value)}
            size="small"
            variant="outlined"
          />
        </Tooltip>
      ),
    },
    { 
      field: 'table_name', 
      headerName: 'Table', 
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          {getTableIcon(params.value)}
          <Typography variant="body2" sx={{ ml: 1 }} noWrap>
            {humaniseTable(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Chip
            icon={<Visibility />}
            label="View"
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedLog(params.row);
              setViewDialogOpen(true);
            }}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Transparency"
        accent={ACCENTS.silver}
        title="Audit Logs"
        subtitle="Every change to money, members and rules, with who made it and when."
      />

      <Alert severity="info" icon={<Security />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Security Audit Trail:</strong> All system activities are logged for transparency and security.
          Only administrators can access this audit trail.
        </Typography>
      </Alert>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={6} md={3}>
            <StatCard title="Activities" value={summary.summary.total_logs}
              subtitle="Last 30 days" icon={<History />} accent={ACCENTS.indigo} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Users" value={summary.summary.unique_users}
              subtitle="Made changes" icon={<Person />} accent={ACCENTS.teal} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Tables" value={summary.summary.tables_affected}
              subtitle="Modified" icon={<Storage />} accent={ACCENTS.marigold} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Actions" value={summary.summary.unique_actions}
              subtitle="Distinct kinds" icon={<Security />} accent={ACCENTS.silver} />
          </Grid>
        </Grid>
      )}

      {/* Recent Activity Summary */}
      {summary?.recentActivity && (
        <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Most Frequent Activities (Last 7 Days)
          </Typography>
          <Grid container spacing={2}>
            {summary.recentActivity.map((activity, index) => (
              <Grid item key={index}>
                <Chip
                  icon={getActionIcon(activity.action)}
                  label={`${humaniseAction(activity.action)} (${activity.count})`}
                  color={getActionColor(activity.action)}
                  variant="outlined"
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          <FilterList sx={{ mr: 1 }} />
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="User"
              value={filters.userId}
              onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              size="small"
            >
              <MenuItem value="">All Users</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.full_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Table"
              value={filters.tableName}
              onChange={(e) => setFilters({ ...filters, tableName: e.target.value })}
              size="small"
            >
              <MenuItem value="">All Tables</MenuItem>
              <MenuItem value="payments">Payments</MenuItem>
              <MenuItem value="penalties">Penalties</MenuItem>
              <MenuItem value="users">Users</MenuItem>
              <MenuItem value="meetings">Meetings</MenuItem>
              <MenuItem value="rules">Rules</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Action"
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              size="small"
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="CREATE_PAYMENT">Create Payment</MenuItem>
              <MenuItem value="UPDATE_PAYMENT">Update Payment</MenuItem>
              <MenuItem value="DELETE_PAYMENT">Delete Payment</MenuItem>
              <MenuItem value="CREATE_PENALTY">Create Penalty</MenuItem>
              <MenuItem value="PAY_PENALTY">Pay Penalty</MenuItem>
              <MenuItem value="UPDATE_USER">Update User</MenuItem>
              <MenuItem value="RESET_PASSWORD">Reset Password</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="From Date"
              value={filters.fromDate}
              onChange={(newValue) => setFilters({ ...filters, fromDate: newValue })}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="To Date"
              value={filters.toDate}
              onChange={(newValue) => setFilters({ ...filters, toDate: newValue })}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Limit"
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
              size="small"
            >
              <MenuItem value={50}>50 records</MenuItem>
              <MenuItem value={100}>100 records</MenuItem>
              <MenuItem value={200}>200 records</MenuItem>
              <MenuItem value={500}>500 records</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Audit Logs Table */}
      <Paper sx={{ width: '100%' }}>
        <ResponsiveDataGrid
          mobile={{ title: 'action', hide: ['id'] }}
          rows={logs}
          columns={columns}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          checkboxSelection={false}
          disableRowSelectionOnClick
          onRowClick={(params) => { setSelectedLog(params.row); setViewDialogOpen(true); }}
          autoHeight
          loading={loading}
          slots={{ noRowsOverlay: NoRows }}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            // v6 collapses the no-rows overlay under autoHeight, clipping it
            '& .MuiDataGrid-overlayWrapper': { minHeight: 300 },
            '& .MuiDataGrid-overlayWrapperInner': { minHeight: 300 },
            '& .MuiDataGrid-cell:hover': { color: 'primary.main' },
          }}
        />
      </Paper>

      {/* Log Detail Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Audit Log Details - #{selectedLog?.id}
            </Typography>
            <IconButton onClick={() => setViewDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedLog && (
            <Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText
                    primary="User"
                    secondary={selectedLog.user_name || 'System'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><History /></ListItemIcon>
                  <ListItemText
                    primary="Action"
                    secondary={selectedLog.action}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>{getTableIcon(selectedLog.table_name)}</ListItemIcon>
                  <ListItemText
                    primary="Table"
                    secondary={selectedLog.table_name}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><AccountCircle /></ListItemIcon>
                  <ListItemText
                    primary="Record ID"
                    secondary={selectedLog.record_id || 'N/A'}
                  />
                </ListItem>
              </List>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Timestamp"
                    secondary={dayjs(selectedLog.created_at).format('MMMM D, YYYY [at] HH:mm:ss')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="IP Address"
                    secondary={selectedLog.ip_address || 'Unknown'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="User Agent"
                    secondary={
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {selectedLog.user_agent || 'Unknown'}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>

          {/* Data Changes */}
          <Box sx={{ mt: 3 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" color="error.main">Previous Values</Typography>
                {!selectedLog.old_values && (
                  <Chip label="No previous data" size="small" sx={{ ml: 2 }} />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Paper sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'error.main',
                  borderRadius: 1
                }}>
                  {renderJsonViewer(selectedLog.old_values, 'old')}
                </Paper>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" color="success.main">New Values</Typography>
                {!selectedLog.new_values && (
                  <Chip label="No new data" size="small" sx={{ ml: 2 }} />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Paper sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'success.main',
                  borderRadius: 1
                }}>
                  {renderJsonViewer(selectedLog.new_values, 'new')}
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;